import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import StatusChip from "components/Chips/StatusChip";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchIssueToLabour } from "slices/Warehouse/Dispatch/IssueToLabour/thunk";
import Swal from "sweetalert2";
import { hideLoading, showCustomSwal } from "utils/swalHelper";

type DispatchProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const Dispatch: React.FC<DispatchProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Issue To Labour";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selectors
  const IssueToLabour = createSelector(
    (state: any) => state.IssueToLabour,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(IssueToLabour);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.dispatch
  );

  const data = list || [];

  // Fetch IssueToLabour list
  useEffect(() => {
    dispatch(
      fetchIssueToLabour({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  const refreshIssueToLabourList = useCallback(() => {
    return dispatch(
      fetchIssueToLabour({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  const revertIssueToLabourDispatch = useCallback(
    (issueToLabour: any) => {
      showCustomSwal({
        title: "Reverting...",
        text: "Please wait while reverting issue to labour dispatch.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      Axios.post(
        `${api?.warehouse?.dispatch?.issue_to_labour?.revert}${issueToLabour.dispatch_id}/revert`
      )
        .then((response) => {
          if (response?.status === 200) {
            showCustomSwal({
              icon: "success",
              title: "Reverted Successfully",
              text:
                response.data?.message ||
                "Issue to labour dispatch reverted successfully!",
            });
            refreshIssueToLabourList();
          } else {
            throw new Error("Invalid API response");
          }
        })
        .catch((error) => {
          hideLoading();
          showCustomSwal({
            icon: "error",
            title: "Failed to revert",
            text: error.response?.data?.message || "Something went wrong!",
          });
          console.error("Issue to labour revert error:", error);
        });
    },
    [refreshIssueToLabourList]
  );

  const handleRevertIssueToLabour = useCallback(
    (issueToLabour: any) => {
      showCustomSwal({
        title: "Revert Issue To Labour?",
        text: "Are you sure you want to revert this issue to labour dispatch?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, revert it!",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          revertIssueToLabourDispatch(issueToLabour);
        }
      });
    },
    [revertIssueToLabourDispatch]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        color: "themeColor",
        permission: (row: any) => {
          return !!(
            canUpdate && row?.dispatch_status?.toLowerCase() !== "completed"
          );
        },
        onClick: (issueToLabour: any) => {
          // Navigate to View page
          navigate(
            `/warehouse-dispatch/IssueToLabour/edit-issueToLabour/${issueToLabour.dispatch_id}`
          );
          // navigate(`/warehouse-dispatch/IssueToLabour/edit-issueToLabour`);
        },
      },
      {
        icon: VisibilityIcon,
        label: "Details",
        permission: canView,
        onClick: (issueToLabour: any) => {
          navigate(
            `/warehouse-dispatch/IssueToLabour/view-details/${issueToLabour.dispatch_id}`
          );
        },
        color: "themeColor",
      },
      {
        icon: UndoIcon,
        label: "Revert",
        permission: (row: any) => {
          return !!(
            canUpdate && row?.dispatch_status?.toLowerCase() !== "completed"
          );
        },
        onClick: (issueToLabour: any) => {
          handleRevertIssueToLabour(issueToLabour);
        },
        color: "error",
      },
    ];
    return items;
  }, [navigate, canView, canUpdate, handleRevertIssueToLabour]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canView;
    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-";
        },
      },
      {
        header: "Dispatch ID",
        accessorKey: "dispatch_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/dispatch_number/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.dispatch_number || "-",
      },
      {
        header: "Issued Date",
        accessorKey: "dispatch_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date
            ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
            : "-";
          return date;
        },
      },
      {
        header: "Issued From",
        accessorKey: "issued_from_warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/issued_from_warehouse_name/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original.issued_from_warehouse_name || "-",
      },
      {
        header: "Labour Name",
        accessorKey: "labour_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/labour_name/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.labour_name || "-",
      },
      {
        header: "Dispatch Status",
        accessorKey: "dispatch_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/issue-to-labour/dropdown/dispatch_status/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => (
          <StatusChip status={row.original.dispatch_status || "-"}>
            {row.original.dispatch_status
              ? row.original.dispatch_status.charAt(0).toUpperCase() +
                row.original.dispatch_status.slice(1)
              : "-"}
          </StatusChip>
        ),
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remarks}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By / Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user_name || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By / Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user_name || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [warehouseIdSearch, DynamicAction, canView]
  );

  return (
    <>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row>
        <Col lg={12}>
          <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default Dispatch;
